import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {gsap} from "gsap";
import {SymbolContainer} from "../reels/SymbolContainer";
import {Game} from "../Game";
import {GameUtils} from "../utils/GameUtils";
import {Mental2SymbolStates} from "../enums/Enum";
import {Config} from "../config/Config";
import {Sound} from "../sound/Sound";
import {NudgeSymbol} from "../reels/NudgeSymbol";

export class WinHighlightView extends PIXI.Container implements StageLayer {

    private _winAnimMap: Map<string, PIXI.spine.Spine[]> = new Map<string, PIXI.spine.Spine[]>();
    private _anticipationTl: gsap.core.Timeline | null;

    constructor() {
        super();
        this.name = "WinHighlightView";
        this.position.set(0, Config.MARGIN_TOP);
    }

    public reset(): void {
        this.removeChildren();
        this._winAnimMap.clear();
    }

    public play(reelId: number, rowId: number, animName: string, splits: boolean[], isInitialWin: boolean = false): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        const symbolContainer: SymbolContainer = GameUtils.getSymbol(reelId, rowId);
        let isWinAtAnyPart: boolean = false;

        const pos: PIXI.Point = GameUtils.getSymbolPos(reelId, rowId);
        if (symbolContainer.isTopHalf) {
            pos.y = pos.y - 49;
        }
        pos.y -= symbolContainer.pivot.y;
        const key: string = reelId + "-" + rowId;
        if (!this._winAnimMap.get(key)) {
            this._winAnimMap.set(key, []);
        }

        let winCount: number = -1;
        symbolContainer.symbols.forEach((sym, index: number) => {
            if (splits[index]) {
                winCount++;
                isWinAtAnyPart = true;
                let winAnim: PIXI.spine.Spine;
                const symArray: PIXI.spine.Spine[] | undefined = this._winAnimMap.get(key);
                if (symArray && symArray.length > winCount) {
                    winAnim = symArray[winCount];
                } else {
                    winAnim = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("winHighlight"));
                    winAnim.height = sym.currentHeight * 0.854;
                    winAnim.name = "WIN_" + reelId + "_" + rowId + "_" + index;
                    this._winAnimMap.get(key)?.push(winAnim);
                    this.addChild(winAnim);
                    winAnim.position.y = pos.y + sym.y;
                    winAnim.position.x = pos.x + sym.x;
                    if (sym instanceof NudgeSymbol) {
                        winAnim.height = Config.SYMBOL_HEIGHT_HALF * 0.854;
                        winAnim.position.y = pos.y - (Config.SYMBOL_HEIGHT_HALF * 0.5);
                    }
                }
                winAnim.alpha = 0;
                tl.add([
                    gsap.to(winAnim, {duration: 0.1, alpha: 1}),
                    GameUtils.createTimelineForAnimation(winAnim, 0, animName, false)
                ], 0);
            } else if (isInitialWin) {
                tl.add(() => {
                    sym.setState(Mental2SymbolStates.NO_WIN)
                }, 0);
            }

        });

        if (isWinAtAnyPart) {
            tl.add(symbolContainer.fadeInXHoleMul(), 0)
        }
        tl.add(() => {
            symbolContainer.fadeOutXHoleMul();
        }, "-=0.01");

        return tl;
    }

    public playBigWinAnticipation(): gsap.core.Timeline {
        if (Game.model.winRatio < ((Game.model.gameData.wasFeatureBuy) ? (100) : ((Game.buyFeatureName === "BOOSTED_BET_4") ? (2400) : (Config.WIN_RATIOS.bigWin[1].ratio)))) { // #355
            return gsap.timeline();
        }

        this._anticipationTl = gsap.timeline();
        this._anticipationTl.add(() => {
            Game.foregroundElements.playReelSetFlicker();
            Game.soundPlayer.playSound(Sound.BIGWIN_ANTICIPATION);
            Game.backgroundElements.playFlickerAnim();
        }, 0)
        this._anticipationTl.add(() => {
            Game.foregroundElements.stopReelSetFlicker();
            Game.backgroundElements.stopFlickerAnim();
        }, GameUtils.bigWinAnticipationDuration());

        return this._anticipationTl;
    }

    public stopBigWinAnticipation(): void {
        if (this._anticipationTl) {
            this._anticipationTl.progress(1);
            this._anticipationTl?.kill();
        }
    }
}